import {
  Grid,
  IconButton,
  KeyboardArrowLeftIcon,
  KeyboardArrowRightIcon,
  makeStyles,
  MenuItem,
  Select,
  Theme,
  Tooltip,
  Typography,
} from '@freighthub/silhouette-core'
import { ChangeEvent, FC, useEffect } from 'react'
import { useI18n } from 'utils/localisation'

import { usePaginator } from '../service/usePaginator'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    width: 'auto',
    flexWrap: 'nowrap',
  },
  selectLabel: {
    margin: theme.spacing(0, 1),
  },
  statusText: {
    whiteSpace: 'nowrap',
  },
}))

export const paginatorTestIds = {
  previousButton: 'paginator_previous_button',
  nextButton: 'paginator_next_button',
}

export type PaginatorProps = {
  next?: (page: number) => void
  previous?: (page: number) => void
  hide?: boolean
  paginatorParams?: {
    total: number
    size?: number
  }
  showRowsPerPage?: boolean
  onChangeRowsPerPage?: (rowsPerPage: number) => void
  trackingIds?: {
    previousPageButton: string
    nextPageButton: string
  }
}

export const Paginator: FC<PaginatorProps> = ({
  paginatorParams,
  next,
  previous,
  onChangeRowsPerPage,
  showRowsPerPage,
  hide,
  trackingIds,
}) => {
  const paginator = usePaginator()
  const classes = useStyles()
  const { translate } = useI18n()
  const rowsPerPageOptions: number[] = [10, 25, 50]
  const { total, pageStartItemNumber, pageEndItemNumber } = paginator

  const statusText = total > 0 ? `${pageStartItemNumber} - ${pageEndItemNumber} of ${total}` : `0 of 0`

  useEffect(() => {
    if (paginatorParams?.total) {
      paginator.setTotal(paginatorParams.total)
    }
  }, [paginatorParams?.total])

  useEffect(() => {
    if (paginatorParams?.size) {
      paginator.setPageSize(paginatorParams.size)
    }
  }, [paginatorParams?.size])

  const handleNext = () => {
    paginator.next()
    if (next) {
      next(paginator.page)
    }
  }

  const handlePrev = () => {
    paginator.previous()
    if (previous) {
      previous(paginator.page)
    }
  }

  const handleChangeRowsPerPage = (event: ChangeEvent<{ value: number }>) => {
    const rowsPerPage = event.target.value
    paginator.setPageSize(rowsPerPage)
    // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    onChangeRowsPerPage(rowsPerPage)
  }

  if (hide) {
    return null
  }

  return (
    <Grid container alignItems="center" className={classes.root}>
      {showRowsPerPage && (
        <>
          <Typography className={classes.selectLabel}>{translate('pagination-rows_per_page')}</Typography>
          <Select
            value={paginator.size}
            onChange={(event) => handleChangeRowsPerPage(event as unknown as ChangeEvent<{ value: number }>)}
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
            }}
          >
            {rowsPerPageOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </>
      )}

      <Tooltip title={translate('pagination-previous_page')} placement="top">
        <span>
          <IconButton
            onClick={handlePrev}
            disabled={paginator.isFirst}
            color="inherit"
            data-testid={paginatorTestIds.previousButton}
            data-tracking-id={trackingIds?.previousPageButton}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Typography variant="body1" className={classes.statusText}>
        {statusText}
      </Typography>

      <Tooltip title={translate('pagination-next_page')} placement="top">
        <span>
          <IconButton
            onClick={handleNext}
            disabled={paginator.isLast}
            color="inherit"
            data-testid={paginatorTestIds.nextButton}
            data-tracking-id={trackingIds?.nextPageButton}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Grid>
  )
}
